import React, {useEffect, useState} from 'react';
import _, {isEmpty} from "lodash";
import SelectHtmlTag from "../rich-text-editor/select-html-tag";
import {ChevronDownIcon, ChevronRightIcon, TagIcon} from '@heroicons/react/24/solid';
import SelectPrompt from "./select-prompt";
import AddEditProductImage from "../../../shopify/components/shopify-blog/add-edit-product-image";
import {processContentLines} from "../../utils/blog-content-util";
import AddEditProductLink from "../../../shopify/components/shopify-blog/add-edit-product-link";
import DeleteContentLine from "../../../shopify/components/shopify-blog/delete-content-line";
import {HTML_META_TYPE_LIST} from "../../constants/content.constant";

const ShowContentLineList = ({
                                 promptsList,
                                 productsList = [],
                                 collectionsList = [],
                                 selectedProductImageList = [],
                                 contentLines,
                                 metaLines = undefined,
                                 setMetaLines = undefined,
                                 cssContainerClass = 'design-blog-post-content-container',
                                 setContentLines = undefined,
                                 prepareHtmlContent = undefined,
                                 openAddTextForContentLine = undefined,
                                 sectionTitlesMap = undefined,
                                 setSectionTitlesMap = undefined,
                             }) => {
    const [refreshLines, setRefreshLines] = useState(false);
    const [selectedContentLineIndex, setSelectedContentLineIndex] = useState(null);

    const changeContentLineType = (contentLineIndex, newType) => {
        const existingContentLines = [...contentLines];
        const existingContentLine = existingContentLines[contentLineIndex];
        existingContentLine.newType = newType;

        processContentLines(existingContentLines);
        setContentLines(existingContentLines);

        processAfterContentLineChange();
    }

    const changeMetaLineType = (metaLineIndex, newType) => {
        const existingMetaLines = [...metaLines];
        const existingMetaLine = existingMetaLines[metaLineIndex];
        existingMetaLine.metaType = newType;
        existingMetaLine.newType = newType;

        processMetaLines(existingMetaLine);
        setMetaLines(existingMetaLines);
    }

    const addLinksToContentLine = (links, contentLineIndex) => {
        const existingContentLines = [...contentLines];
        existingContentLines[contentLineIndex].links = links;

        processContentLines(existingContentLines);
        setContentLines(existingContentLines);

        processAfterContentLineChange();
    }

    const addImageToContentLine = (productImage, contentLineIndex) => {
        const imageLine = {
            key: "P",
            type: "p",
            text: productImage.productTitle,
            html: `<a href="${productImage?.onlineStoreUrl}" target="_blank"><img src="${productImage?.url}" alt="${productImage.productTitle}"></a>`,
            newType: "br",
            isImage: true,
            productImageId: productImage.id,
        }

        const existingContentLines = [...contentLines];
        existingContentLines.splice(contentLineIndex + 1, 0, imageLine);

        processContentLines(existingContentLines);
        setContentLines(existingContentLines);

        processAfterContentLineChange();
    }

    const handleContentSplitHeader = (contentLineIndex) => {
        setRefreshLines(true);
        setSelectedContentLineIndex(contentLineIndex);

        const existingContentLines = [...contentLines];
        const existingContentLine = existingContentLines[contentLineIndex];

        const splitText = existingContentLine.text.split(':');
        const headerText = {
            key: "H2",
            type: "h2",
            text: splitText[0],
            html: `<h2>${splitText[0]}</h2>`,
            newType: "h2",
            isSection: true
        }

        const paragraphText: any = {
            key: "P",
            type: "p",
            text: splitText[1],
            html: `<p>${splitText[1]}</p>`,
            newType: "p",
            section: splitText[0],
        }

        existingContentLines[contentLineIndex] = paragraphText;
        existingContentLines.splice(contentLineIndex, 0, headerText);
        setContentLines(existingContentLines);
        handleShowHideSectionContent(headerText);

        processAfterContentLineChange();
    }

    const handleMetaSplitHeader = (metaLineIndex) => {
        const existingMetaLines = [...metaLines];
        const existingMetaLine = existingMetaLines[metaLineIndex];

        const splitText = existingMetaLine.text.split(':');
        const headerText = {
            key: "H2",
            type: "h2",
            text: splitText[0],
            html: `<h2>${splitText[0]}</h2>`,
            newType: "h2",
            isSection: true
        }

        const paragraphText: any = {
            key: "P",
            type: "p",
            text: splitText[1],
            html: `<p>${splitText[1]}</p>`,
            newType: "p",
            isMeta: true
        }

        if (headerText.text.indexOf('Tags') > -1) {
            paragraphText.metaType = 'tags';
        } else if (headerText.text.indexOf('Meta') > -1) {
            paragraphText.metaType = 'meta';
        } else if (headerText.text.indexOf('Excerpt') > -1) {
            paragraphText.metaType = 'excerpt';
        }

        processMetaLines(paragraphText);
        existingMetaLines[metaLineIndex] = paragraphText;
        setMetaLines(existingMetaLines);
    }

    const processMetaLines = (existingMetaLine) => {
        if (existingMetaLine.metaType === 'tags') {
            existingMetaLine.metaLabel = 'Tags';
        } else if (existingMetaLine.metaType === 'meta') {
            existingMetaLine.metaLabel = 'Meta Description';
        } else if (existingMetaLine.metaType === 'excerpt') {
            existingMetaLine.metaLabel = 'Excerpt';
        }

        if (existingMetaLine.metaType) {
            existingMetaLine.newType = existingMetaLine.metaType;
            existingMetaLine.isMeta = true;
        }
    }

    const handleRemoveMetaLine = (metaLineIndex) => {
        const existingMetaLines = [...metaLines];
        existingMetaLines.splice(metaLineIndex, 1);
        setMetaLines(existingMetaLines);
    }

    const handleRemoveContentLine = (contentLineIndex) => {
        setRefreshLines(true);
        setSelectedContentLineIndex(contentLineIndex);

        const existingContentLines = [...contentLines];
        existingContentLines.splice(contentLineIndex, 1);
        setContentLines(existingContentLines);

        processAfterContentLineChange();
    }

    const handleShowHideSectionContent = (contentLine) => {
        const existingSectionTitlesMap = {...sectionTitlesMap};
        existingSectionTitlesMap[contentLine.text] = !existingSectionTitlesMap[contentLine.text];
        setSectionTitlesMap(existingSectionTitlesMap);
    }

    const processAfterContentLineChange = () => {
        if (prepareHtmlContent) {
            prepareHtmlContent();
        }

        setTimeout(() => {
            setRefreshLines(false);
            setSelectedContentLineIndex(null);
        }, 1);
    }

    return (
        <>
            {(contentLines?.length > 0 || metaLines?.length > 0) &&
                <div
                    className={`w-full border border-gray-400 bg-gray-400 rounded-lg px-4 py-2 overflow-y-scroll ${cssContainerClass}`}>

                    {contentLines?.map((contentLine, contentLineIndex) => (
                        <div key={`contentLine-${contentLineIndex}`}
                             className="flex justify-start">

                            {(isEmpty(selectedContentLineIndex) || (!isEmpty(selectedContentLineIndex) && selectedContentLineIndex <= contentLineIndex && !refreshLines)) &&

                                <>
                                    {((contentLine.isSection || !contentLine.section) || (contentLine.section && sectionTitlesMap[contentLine.section])) &&

                                        <>
                                            <div
                                                className="flex justify-center self-center text-2xl text-white w-10">{contentLineIndex + 1}</div>
                                            <div
                                                className={`w-full m-2 pl-4 pr-3 py-3 border border-gray-400 bg-white shadow-lg rounded-xl ${_.isEmpty(contentLine.section) ? '' : 'ml-16'}`}>

                                                <div className={'flex items-center justify-between'}>
                                                    {contentLine.isSection &&
                                                        <div className={'mr-4'}>
                                                            <button
                                                                className="button-pink-outline px-2 py-1 shadow-lg"
                                                                onClick={() => handleShowHideSectionContent(contentLine)}>
                                                                {sectionTitlesMap[contentLine.text] ?
                                                                    <ChevronDownIcon className="h-12 w-6"/> :
                                                                    <ChevronRightIcon className="h-12 w-6"/>
                                                                }
                                                            </button>
                                                        </div>
                                                    }

                                                    <div className={`w-full`}>
                                                        <div
                                                            className={`flex justify-start text-gray-900 ${contentLine.isSection ? 'font-bold text-pink-500' : ''} ${contentLine.isImage ? 'font-semibold text-purple-500' : ''}`}>
                                                            {contentLine.isImage &&
                                                                <TagIcon className="h-6 w-6 mr-2"/>
                                                            }
                                                            <div
                                                                className={''}>{contentLine.text}</div>
                                                        </div>

                                                        {contentLine.links && contentLine.links.length > 0 &&
                                                            <div className={'my-1'}>
                                                                {contentLine.links.map((link, linkIndex) => (
                                                                    <div key={linkIndex}>
                                                                        <a href={link.url}
                                                                           className={'pr-2 underline text-blue-500'}>{link.url}</a>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        }

                                                        <div className={'flex items-center'}>
                                                            <SelectHtmlTag
                                                                contentLine={contentLine}
                                                                contentLineIndex={contentLineIndex}
                                                                changeContentLineType={changeContentLineType}/>

                                                            {
                                                                <div
                                                                    className={'text-right flex justify-end min-w-fit'}>
                                                                    <div className={'m-1'}>
                                                                        <button
                                                                            className="button-sky-outline px-2 py-1"
                                                                            onClick={() => openAddTextForContentLine(contentLine, contentLineIndex)}>
                                                                            Add Text
                                                                        </button>
                                                                    </div>

                                                                    {contentLine.containsColon && _.isEmpty(contentLine.section) &&
                                                                        <div className={'m-1'}>
                                                                            <button
                                                                                className="button-outline-gray px-2 py-1"
                                                                                onClick={() => handleContentSplitHeader(contentLineIndex)}>
                                                                                Split Header
                                                                            </button>
                                                                        </div>
                                                                    }

                                                                    <div className={'m-1'}>
                                                                        {selectedProductImageList && selectedProductImageList.length > 0 &&
                                                                            <>
                                                                                {!contentLine?.isImage &&
                                                                                    <AddEditProductImage
                                                                                        afterProductImageSelected={(event) => {
                                                                                            addImageToContentLine(event, contentLineIndex);
                                                                                        }}
                                                                                        productImageList={selectedProductImageList}/>
                                                                                }
                                                                            </>
                                                                        }
                                                                    </div>

                                                                    {!contentLine.isSection &&
                                                                        <div className={'m-1'}>
                                                                            <AddEditProductLink
                                                                                isAddProductLink={true}
                                                                                contentLine={contentLine}
                                                                                productsList={productsList}
                                                                                collectionsList={collectionsList}
                                                                                afterProductLinksPrepared={(event) => {
                                                                                    addLinksToContentLine(event, contentLineIndex);
                                                                                }}/>
                                                                        </div>
                                                                    }

                                                                    <div className={'m-1'}>
                                                                        <DeleteContentLine
                                                                            contentLine={contentLine}
                                                                            contentLineIndex={contentLineIndex}
                                                                            handleRemoveContentLine={handleRemoveContentLine}/>
                                                                    </div>
                                                                </div>
                                                            }
                                                        </div>

                                                        {contentLine.isSection &&
                                                            <div>
                                                                <SelectPrompt
                                                                    subTypeList={['Section']}
                                                                    promptsList={promptsList}
                                                                    title={contentLine.text}/>
                                                            </div>
                                                        }

                                                    </div>
                                                </div>

                                            </div>
                                        </>

                                    }
                                </>

                            }

                        </div>
                    ))}

                    {metaLines?.length > 0 &&
                        <>

                            <div className={'flex justify-start my-4'}>
                                <div className={'text-white font-bold text-lg ml-12'}>
                                    Excerpts/Tags/Meta Description
                                </div>
                            </div>

                            {metaLines?.map((metaLine, metaLineIndex) => (
                                <div key={`metaLine-${metaLineIndex}`}
                                     className="flex justify-start">

                                    {!refreshLines &&
                                        <>
                                            <div
                                                className="flex justify-center self-center text-2xl text-white w-10">{metaLineIndex + 1}</div>
                                            <div
                                                className={`w-full m-2 pl-4 pr-3 py-3 border border-gray-400 bg-white shadow-lg rounded-xl ${_.isEmpty(metaLine.section) ? '' : 'ml-16'}`}>

                                                <div className={'flex items-center justify-between'}>
                                                    <div className={`w-full`}>
                                                        {metaLine?.isMeta &&
                                                            <div
                                                                className={`flex justify-start text-gray-900 font-bold text-sky-500`}>
                                                                <div className={''}>{metaLine.metaLabel}</div>
                                                            </div>
                                                        }
                                                        <div
                                                            className={`flex justify-start text-gray-900 ${metaLine.isSection ? 'font-bold text-pink-500' : ''}`}>
                                                            <div className={''}>{metaLine.text}</div>
                                                        </div>

                                                        <div className={'flex justify-between items-center'}>
                                                            {!metaLine.isSection &&
                                                                <SelectHtmlTag
                                                                    contentLine={metaLine}
                                                                    contentLineIndex={metaLineIndex}
                                                                    elementTypeList={HTML_META_TYPE_LIST}
                                                                    changeContentLineType={changeMetaLineType}/>
                                                            }

                                                            {
                                                                <div
                                                                    className={'text-right flex justify-end min-w-fit'}>

                                                                    {metaLine.containsColon && !metaLine.isMeta &&
                                                                        <div className={'m-1'}>
                                                                            <button
                                                                                className="button-outline-gray px-2 py-1"
                                                                                onClick={() => handleMetaSplitHeader(metaLineIndex)}>
                                                                                Split Header
                                                                            </button>
                                                                        </div>
                                                                    }

                                                                    <div className={'m-1'}>
                                                                        <DeleteContentLine
                                                                            contentLine={metaLine}
                                                                            contentLineIndex={metaLineIndex}
                                                                            handleRemoveContentLine={handleRemoveMetaLine}/>
                                                                    </div>
                                                                </div>
                                                            }
                                                        </div>

                                                    </div>
                                                </div>

                                            </div>

                                        </>
                                    }

                                </div>
                            ))}
                        </>
                    }

                </div>
            }

        </>
    )
}

export default ShowContentLineList
